import "@abraham/reflection";

import "./themes/default/default.css";
import "./themes/globals.css";

import {
    InstagramApplicationConfigInterface,
    InstagramApplicationConfigToken,
} from "@apps/instagram/InstagramApplicationConfig";
import {
    TikTokApplicationConfigInterface,
    TikTokApplicationConfigToken,
} from "@apps/tiktok/TikTokApplicationConfig";
import CustomerInfoFromJsonProvider from "@lib/CustomerInfoProvider/CustomerInfoFromJsonProvider";
import GrowaveConfigurationFromJsonProvider from "@lib/GrowaveConfigurationProvider/GrowaveConfigurationFromJsonProvider";
import HistoryPatcher from "@lib/HistoryPatcher";
import { SimpleLocalizationInfoProvider } from "@lib/LocalizationInfoProvider/SimpleLocalizationInfoProvider";
import { ProductIdFromAdditionalInfoProvider } from "@lib/ProductIdManager/ProductIdFromAdditionalInfoProvider";
import { ProductIdFromSdkProvider } from "@lib/ProductIdManager/ProductIdFromSdkProvider";
import { ProductIdManager } from "@lib/ProductIdManager/ProductIdManager";
import { ProductVariantIdFromSdkProvider } from "@lib/ProductVariantIdManager/ProductVariantIdFromSdkProvider";
import Timer from "@lib/timer";
import { getJsonFromScript } from "@lib/utils";
import { AnalyticsEventsCollectorModule } from "@modules/analytics_events_collector/AnalyticsEventsCollectorModule";
import { AuthenticationModule } from "@modules/authentication/AuthenticationModule";
import { CommonModule } from "@modules/common_module/CommonModule";
import { ProductsModule } from "@modules/products/ProductsModule";
import { dispatchSdkEvent } from "@modules/sdk/helpers";
import { SdkModule } from "@modules/sdk/SdkModule";
import MutationObserverInjector from "@ui-utils/Injector/MutationObserverInjector";
import {
    DependencyContainer,
    container,
    instanceCachingFactory,
} from "tsyringe";

import { ModalManager } from "@components/Modal/ModalManager";

import { GW_ADDITIONAL_INFO_INSTAGRAM_SELECTOR } from "./constants";
import { CURRENT_APP_INSTAGRAM } from "./constants/current_app";
import { GP_GW_REFFERER } from "./constants/get_params";
import { MinilogLogger } from "./lib/Logger";
import {
    customerInfoProviderToken,
    dispatchSdkEventToken,
    globalLoggerToken,
    globalProductIdManagerToken,
    growaveConfigurationProviderToken,
    growaveConfigurationToken,
    gwStorefrontAppInfoToken,
    isEmbeddedModeToken,
    localStorageToken,
    localizationInfoProviderToken,
    modalManagerToken,
} from "./tokens";
import { checkIsEmbeddedMode } from "./utils/checkIsEmbeddedMode";

function __gwMain() {
    container.register(globalLoggerToken, {
        useFactory: instanceCachingFactory(() => {
            return new MinilogLogger("global");
        }),
    });
    container
        .resolve(globalLoggerToken)
        .info("Growave main script is initialized!");

    container.register(gwStorefrontAppInfoToken, {
        useFactory: instanceCachingFactory(() => {
            return {
                appName: __APP_NAME__,
                version: __VERSION__,
                currentApp: CURRENT_APP_INSTAGRAM,
                appProxyPrefix: `${
                    window.Shopify?.routes?.root || "/"
                }apps/ssw-instagram`,
                appBaseUrl:
                    __APP_ENV__ === "development"
                        ? "https://app.dev.growave.io"
                        : "https://app.growave.io",
                translationsServiceBaseUrl:
                    __APP_ENV__ === "development"
                        ? "https://translations.dev.growave.io"
                        : "https://translations.growave.io",
            };
        }),
    });

    // Additional info data
    const additionalInfoData = getJsonFromScript(
        GW_ADDITIONAL_INFO_INSTAGRAM_SELECTOR
    );

    // (utils) growaveConfigurationProvider
    container.register(growaveConfigurationProviderToken, {
        useFactory: instanceCachingFactory(() => {
            return new GrowaveConfigurationFromJsonProvider(
                container.resolve(globalLoggerToken),
                additionalInfoData
            );
        }),
    });

    container.register(growaveConfigurationToken, {
        useFactory: instanceCachingFactory((di: DependencyContainer) => {
            return di
                .resolve(growaveConfigurationProviderToken)
                .getConfiguration();
        }),
    });

    container.register(localizationInfoProviderToken, {
        useFactory: instanceCachingFactory(() => {
            return new SimpleLocalizationInfoProvider(
                container.resolve(growaveConfigurationToken).countryIsoCode,
                container.resolve(growaveConfigurationToken).languageIsoCode,
                container.resolve(growaveConfigurationToken).currencyIsoCode,
                container.resolve(growaveConfigurationToken).gwI18nLocale
            );
        }),
    });

    // localStorage
    container.register(localStorageToken, { useValue: localStorage });

    // (utils) historyPatcher
    const historyPatcher = new HistoryPatcher(
        [GP_GW_REFFERER],
        window.history,
        container.resolve(CommonModule).exports.locationController
    );
    historyPatcher.patch();

    // Is embedded mode
    const isEmbeddedMode = checkIsEmbeddedMode(additionalInfoData);
    container.registerInstance(isEmbeddedModeToken, isEmbeddedMode);

    // ProductIdManager
    container.register(ProductIdFromSdkProvider, {
        useFactory: instanceCachingFactory(() => {
            return new ProductIdFromSdkProvider();
        }),
    });
    container.register(globalProductIdManagerToken, {
        useFactory: instanceCachingFactory(() => {
            const productIdManager = new ProductIdManager();
            productIdManager.registerProvider(
                new ProductIdFromAdditionalInfoProvider(additionalInfoData)
            );
            return productIdManager;
        }),
    });

    // ModalManager
    const modalManager = new ModalManager(dispatchSdkEvent);
    container.registerInstance(modalManagerToken, modalManager);

    container.register(dispatchSdkEventToken, { useValue: dispatchSdkEvent });

    // ProductVariantsIdManager
    container.register(ProductVariantIdFromSdkProvider, {
        useFactory: instanceCachingFactory(() => {
            return new ProductVariantIdFromSdkProvider();
        }),
    });

    // (utils) CustomerInfoProvider
    const customerInfoProvider = new CustomerInfoFromJsonProvider(
        container.resolve(globalLoggerToken),
        additionalInfoData
    );
    customerInfoProvider.readData();
    container.registerInstance(customerInfoProviderToken, customerInfoProvider);

    // container.resolve(AnalyticsEventsCollectorModule).exports.startSending();
    container.register(SdkModule, {
        useFactory: instanceCachingFactory(() => new SdkModule()),
    });
    container.resolve(AuthenticationModule);
    container.resolve(ProductsModule);
    container.register(MutationObserverInjector, {
        useFactory: instanceCachingFactory(() => {
            return new MutationObserverInjector(
                MutationObserver,
                document.body
            );
        }),
    });

    container
        .resolve(AnalyticsEventsCollectorModule)
        .exports.eventsCollector.pushEvent(
            container
                .resolve(AnalyticsEventsCollectorModule)
                .exports.eventsFactory.createChunkLoaded("main", -1)
        );

    // Instagram
    container.register<InstagramApplicationConfigInterface>(
        InstagramApplicationConfigToken,
        {
            useValue: {
                placeholderSelector: ".gw-instagram-gallery-placeholder",
            },
        }
    );
    const startLoadingInstagramApplicationTimer = new Timer();
    void import(
        /*webpackChunkName: "InstagramApplication"*/ "@apps/instagram/InstagramApplication"
    ).then(({ InstagramApplication }) => {
        const duration = startLoadingInstagramApplicationTimer.ready();
        container
            .resolve(AnalyticsEventsCollectorModule)
            .exports.eventsCollector.pushEvent(
                container
                    .resolve(AnalyticsEventsCollectorModule)
                    .exports.eventsFactory.createChunkLoaded(
                        "InstagramApplication",
                        duration
                    )
            );
        const instagramApplication = container.resolve(InstagramApplication);
        instagramApplication.init();
    });

    // Tiktok
    container.register<TikTokApplicationConfigInterface>(
        TikTokApplicationConfigToken,
        {
            useValue: {
                placeholderSelector: ".gw-tiktok-gallery-placeholder",
            },
        }
    );
    const startLoadingTikTokApplicationTimer = new Timer();
    void import(
        /*webpackChunkName: "TikTokApplication" */ "@apps/tiktok/TikTokApplication"
    ).then(({ TikTokApplication }) => {
        const duration = startLoadingTikTokApplicationTimer.ready();
        container
            .resolve(AnalyticsEventsCollectorModule)
            .exports.eventsCollector.pushEvent(
                container
                    .resolve(AnalyticsEventsCollectorModule)
                    .exports.eventsFactory.createChunkLoaded(
                        "TikTokApplication",
                        duration
                    )
            );

        const tiktokApplication = container.resolve(TikTokApplication);
        tiktokApplication.init();
    });
}

void __gwMain();
